/**
 * Invitations: the only way a second person gets into a tenant.
 *
 * The asymmetry here is the point. An owner *creates* an invite straight from
 * the client — the rules can check `isOwner(tenantId)` on the incoming document,
 * so a function would add nothing but a cold start. *Accepting* one is a
 * callable, because it writes `members/{uid}` and the invitee's
 * `users/{uid}.tenantIds` in one transaction across two trees no client may
 * touch. A partial apply there leaves someone who is a member of a tenant they
 * cannot see. See functions/src/membership.ts.
 *
 * Query keys are keyed by the email an invite is addressed to, not by tenant:
 * an invitation is the one thing a person can read *before* they belong
 * anywhere, and there is no tenant to scope it by yet.
 */
import {
  INVITE_TTL_MS,
  type Invite,
  type Locale,
  paths,
  type Role,
  type ViewScope,
} from '@puppy-doctor/platform';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { collection, deleteDoc, doc, getDocs, orderBy, query, setDoc, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { SNAPSHOT_OPTIONS } from '@/api/converter.ts';
import { inviteConverter } from '@/api/converters.ts';
import { useLiveCollection } from '@/api/useLiveQuery.ts';
import { db, functions } from '@/firebase.ts';

/**
 * Google hands back a lowercase email in the token, and `acceptInvite` compares
 * the stored address to it with `!==`. An owner who types `Ala@Example.com`
 * would otherwise create an invitation that can never be accepted and gives no
 * hint why.
 */
export const normalizeEmail = (email: string): string => email.trim().toLowerCase();

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

const invitesForEmailQuery = (email: string) =>
  query(
    collection(db, paths.invites()).withConverter(inviteConverter),
    where('email', '==', email),
    where('acceptedAt', '==', null),
  );

export const invitesForEmailQueryOptions = (email: string) =>
  queryOptions({
    queryKey: ['invites', 'for', email] as const,
    queryFn: async (): Promise<Invite[]> => {
      const snap = await getDocs(invitesForEmailQuery(email));
      return snap.docs.map((d) => d.data(SNAPSHOT_OPTIONS));
    },
  });

/**
 * Every unaccepted invitation addressed to this person, across every tenant.
 *
 * Expired ones are filtered here rather than in the query: `expiresAt < now` is
 * a third inequality the index would have to carry, and an expired invitation is
 * worth showing as expired — "ask for a new one" is a better dead end than an
 * empty screen the invitee cannot explain.
 */
export const useInvitesForEmail = (email: string) => {
  useLiveCollection(['invites', 'for', email], invitesForEmailQuery(email));
  return useQuery(invitesForEmailQueryOptions(email));
};

const invitesForTenantQuery = (tenantId: string) =>
  query(
    collection(db, paths.invites()).withConverter(inviteConverter),
    where('tenantId', '==', tenantId),
    orderBy('createdAt', 'desc'),
  );

export const invitesForTenantQueryOptions = (tenantId: string) =>
  queryOptions({
    queryKey: ['invites', 'tenant', tenantId] as const,
    queryFn: async (): Promise<Invite[]> => {
      const snap = await getDocs(invitesForTenantQuery(tenantId));
      return snap.docs.map((d) => d.data(SNAPSHOT_OPTIONS));
    },
  });

/** The owner's view: who has been invited here and has not arrived yet. */
export const useInvitesForTenant = (tenantId: string) => {
  useLiveCollection(['invites', 'tenant', tenantId], invitesForTenantQuery(tenantId));
  return useQuery(invitesForTenantQueryOptions(tenantId));
};

// ---------------------------------------------------------------------------
// Writing
// ---------------------------------------------------------------------------

export type CreateInviteInput = {
  tenantId: string;
  /** Denormalised so the invitee can see whose kennel this is before joining. */
  tenantName: string;
  email: string;
  role: Role;
  /**
   * What this person will be allowed to see once they join — see `ViewScopes` in
   * platform. Only an observer is scoped by it; for every other role the caller
   * passes all of them, and nothing reads it.
   */
  scopes: readonly ViewScope[];
  invitedBy: string;
  /**
   * Which language to write the invitation email in — the language the person
   * doing the inviting is reading right now.
   *
   * Passed in rather than read from i18next here, because this file is the data
   * layer and knows nothing about the UI. The caller is a screen; it knows.
   */
  locale: Locale;
};

/**
 * Not awaited, and the id comes back synchronously — the offline write rule.
 * An owner on a phone in a garage can still invite somebody; the invitation
 * lands when the phone next sees a network, and the listener has already shown
 * it in the pending list. See api/mutations.ts.
 */
export const createInvite = ({
  tenantId,
  tenantName,
  email,
  role,
  scopes,
  invitedBy,
  locale,
}: CreateInviteInput): string => {
  const ref = doc(collection(db, paths.invites()));
  const now = Date.now();

  void setDoc(ref, {
    tenantId,
    tenantName,
    email: normalizeEmail(email),
    role,
    scopes,
    invitedBy,
    createdAt: now,
    expiresAt: now + INVITE_TTL_MS,
    acceptedAt: null,
    locale,
    // Both null, and the rules insist on it: the function that hands the mail to
    // an SMTP server is the only thing allowed to say it went out. See
    // functions/src/invites.ts.
    sentAt: null,
    sendError: null,
  }).catch(reportWriteFailure);

  return ref.id;
};

/**
 * Where an invitation is accepted, as this build of the app serves it.
 *
 * Built from the running origin rather than from a configured base URL, because
 * this one is for a human to copy: whatever address the owner is looking at the
 * app on is the address the invitation should be sent to. The *email* builds its own
 * from `APP_URL` in the functions parameters, which is the deployed origin and
 * not, say, `localhost:4318`.
 */
export const joinUrl = (inviteId: string): string =>
  `${window.location.origin}/join/${inviteId}`;

/**
 * Send the invitation email again, and give it another full term.
 *
 * Awaited, unlike almost every write in this app, and for the same reason
 * `acceptInvite` is: there is nothing local about it. The whole point is what a
 * mail server said, the owner is watching a button, and offline there is nothing
 * to report yet. See functions/src/invites.ts.
 */
export const resendInvite = async (inviteId: string): Promise<void> => {
  const call = httpsCallable<{ inviteId: string }, { ok: boolean }>(functions, 'resendInvite');
  await call({ inviteId });
};

export const revokeInvite = (inviteId: string): void => {
  void deleteDoc(doc(db, paths.invite(inviteId))).catch(reportWriteFailure);
};

/**
 * The exception to the never-await rule, and it is honest: joining a tenant is
 * a server transaction, there is nothing to show until it has run, and it
 * genuinely cannot happen offline. The membership it writes arrives back
 * through TenantProvider's listener on `users/{uid}` — nothing here has to
 * refresh anything.
 */
export const acceptInvite = async (inviteId: string): Promise<void> => {
  const call = httpsCallable<{ inviteId: string }, { tenantId: string; role: Role }>(
    functions,
    'acceptInvite',
  );
  await call({ inviteId });
};

const reportWriteFailure = (error: unknown): void => {
  console.error('[puppy-doctor] write rejected', error);
};
